import { Box, Button, Checkbox, FormControlLabel, Popover, Stack, Typography } from '@mui/material'
import { t } from 'i18next'
import CollapsibleSection from '../Button/CollapsibleSection'
import { useLeftPadding } from '../../store/redux/hooks'
import { PushNotificationFilterType } from '../Sidebar/notifications/NotificationSidebar/NotificationSidebar'
import { useEffect, useState } from 'react'
import { notificationsReasonArray } from '../../store/types/enums/NotificationReason'

type FilterProps = {
  isOpen: boolean
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  anchorEl: HTMLElement | null
  selectedFilters: PushNotificationFilterType
  setSelectedFilters: React.Dispatch<React.SetStateAction<PushNotificationFilterType>>
}

const FilterNotificationsPopup = ({
  isOpen,
  setIsOpen,
  anchorEl,
  selectedFilters,
  setSelectedFilters,
}: FilterProps) => {
  const leftPadding = useLeftPadding()

  const reasons = notificationsReasonArray

  const [newSelectedFilters, setNewSelectedFilters] = useState<PushNotificationFilterType>(selectedFilters)

  useEffect(() => {
    if (isOpen) {
      setNewSelectedFilters(selectedFilters)
    }
  }, [isOpen, selectedFilters])

  const handleFilter = () => {
    setSelectedFilters(newSelectedFilters)
    setIsOpen(false)
  }

  const handleClear = () => {
    setNewSelectedFilters(prev => ({
      ...prev,
      isRead: [],
      reason: [],
    }))
    setSelectedFilters(prev => ({
      ...prev,
      isRead: [],
      reason: [],
    }))
    setIsOpen(false)
  }

  const handleClose = (reason?: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'backdropClick') {
      setNewSelectedFilters(selectedFilters)
      setIsOpen(false)
    } else {
      setIsOpen(false)
    }
  }

  return (
    <Popover
      open={isOpen}
      onClose={(_, reason) => handleClose(reason)}
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      sx={{ width: '100%', maxWidth: 450, marginTop: 2 }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          pt: 3,
          pb: 3,
          pr: leftPadding,
          gap: 2,
          maxHeight: '500px',
        }}
      >
        <Typography
          variant="h5"
          component="span"
          alignItems="center"
          color="text.highEmphasis"
          sx={{ pl: leftPadding }}
        >
          {t('components.notificationSidebar.filtering.title')}
        </Typography>

        <Box
          sx={{
            overflowY: 'auto',
            maxHeight: '350px',
            pr: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            width: '100%',
          }}
        >
          <CollapsibleSection text={t('components.notificationSidebar.filtering.status')}>
            <FormControlLabel
              sx={{ pl: leftPadding }}
              control={
                <Checkbox
                  checked={newSelectedFilters.isRead.includes(false)}
                  onChange={(_, checked) =>
                    setNewSelectedFilters(prev => ({
                      ...prev,
                      isRead: checked ? [...prev.isRead, false] : prev.isRead.filter(r => r !== false),
                    }))
                  }
                />
              }
              label={t('components.notificationSidebar.filtering.unseen')}
            />

            <FormControlLabel
              sx={{ pl: leftPadding }}
              control={
                <Checkbox
                  checked={newSelectedFilters.isRead.includes(true)}
                  onChange={(_, checked) =>
                    setNewSelectedFilters(prev => ({
                      ...prev,
                      isRead: checked ? [...prev.isRead, true] : prev.isRead.filter(r => r !== true),
                    }))
                  }
                />
              }
              label={t('components.notificationSidebar.filtering.seen')}
            />
          </CollapsibleSection>

          <CollapsibleSection text={t('components.notificationSidebar.filtering.reason')}>
            {reasons.map(reason => (
              <FormControlLabel
                key={reason.key}
                sx={{ pl: leftPadding }}
                control={
                  <Checkbox
                    checked={newSelectedFilters.reason.includes(reason.key)}
                    onChange={(_, checked) =>
                      setNewSelectedFilters(prev => ({
                        ...prev,
                        reason: checked ? [...prev.reason, reason.key] : prev.reason.filter(r => r !== reason.key),
                      }))
                    }
                  />
                }
                label={reason.label}
              />
            ))}
          </CollapsibleSection>
        </Box>

        <Stack direction="row" spacing={2} justifyContent="center" sx={{ width: '100%', pl: leftPadding }}>
          <Button variant="text" onClick={handleClear}>
            {t('components.upcomingVacation.filter.clear')}
          </Button>
          <Button variant="contained" onClick={handleFilter}>
            {t('components.upcomingVacation.filter.apply')}
          </Button>
        </Stack>
      </Box>
    </Popover>
  )
}

export default FilterNotificationsPopup
